import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LicenceSnapshot } from '../../api/types';
import {
  anchorLicence,
  createDayWatcher,
  licenceBannerView,
  loadDismissedDay,
  saveDismissedDay,
  type LicenceBannerView,
} from './licenceCountdown';

/**
 * MVP-513: the React wiring for the venue-facing licence countdown. All of the thinking is in
 * `licenceCountdown.ts` (including why the count is anchored to the server and then ticked locally
 * rather than re-fetched); this file is the three pieces of state that thinking needs.
 *
 * Same split as `useStorefrontMode.ts`: a pure module nobody has to mount to test, plus a hook thin
 * enough that there is nothing left in it to get wrong.
 */
export interface LicenceCountdown {
  /** What to render, or null: outside the window, dismissed for today, or no licence at all. */
  view: LicenceBannerView | null;
  /** Hide the strip for the CURRENT Bangkok day only. It comes back by itself tomorrow. */
  dismiss: () => void;
}

export function useLicenceCountdown(licence: LicenceSnapshot | null): LicenceCountdown {
  // `Date.now()` inside the memo, not in a ref: the anchor must be re-taken whenever a new payload
  // arrives (a re-login, or a `whoami` on a second mount), and the payload's identity is the only
  // signal that one has. A stale anchor would keep counting from the old server answer.
  const anchor = useMemo(() => anchorLicence(licence, Date.now()), [licence]);

  // The instant the strip is currently describing. It moves exactly once per Bangkok day.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => createDayWatcher(setNowMs), []);

  const [dismissedDay, setDismissedDay] = useState<string | null>(loadDismissedDay);

  const view = licenceBannerView(anchor, nowMs);

  const dismiss = useCallback(() => {
    if (!view) return;
    setDismissedDay(view.dayKey);
    saveDismissedDay(view.dayKey);
  }, [view]);

  // The dismissal is compared against the day the strip is FOR, so crossing midnight un-dismisses it
  // without anything having to clear the stored value — see `licenceCountdown.ts`'s storage comment.
  return { view: view && view.dayKey !== dismissedDay ? view : null, dismiss };
}
